import { memo, useEffect } from 'react'
import {
  Button,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Divider,
  Checkbox,
  Center,
} from '@chakra-ui/react'
import { Search } from 'components/icons'
import { useState } from 'react'

type OptionProps = {
  label: string
  value?: string
}

type CustomMultipleItemSelectionProps = {
  options: OptionProps[]
  label: string
  setValues: (values: any) => void
  reset?: boolean
  fullWidth?: boolean
  isLoading?: boolean
  selectedOptionsDefaultValues?: any
}

const CustomMultipleItemSelection = ({
  options,
  label,
  setValues,
  reset,
  fullWidth,
  isLoading = false,
  selectedOptionsDefaultValues,
}: CustomMultipleItemSelectionProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [filterInput, setFilterInput] = useState<string>('')
  const [selectedOptions, setSelectedOptions] = useState<OptionProps[]>([])
  const [filteredQuantitative, setFilteredQuantitative] = useState(selectedOptionsDefaultValues?.length || 0)

  function onSelect(newSelectedOption: OptionProps) {
    const newArray = [...selectedOptions, newSelectedOption]
    const newArraySorted = newArray.sort(function (a, b) {
      return a.label < b.label ? -1 : a.label > b.label ? 1 : 0
    })
    setSelectedOptions(newArraySorted)
  }

  function onDeselect(newDeselectedOption: OptionProps) {
    setSelectedOptions((l) => l.filter((item) => item.label !== newDeselectedOption.label))
  }

  function onSubmit() {
    const arrayForSubmit: any = []
    selectedOptions.forEach((element) => {
      arrayForSubmit.push(element.value)
    })
    setFilteredQuantitative(arrayForSubmit.length)
    setValues(arrayForSubmit)
    onClose()
  }

  function cleanFilter() {
    // onClose()
    setSelectedOptions([])
    // setValues([])
    setFilteredQuantitative(0)
  }

  useEffect(() => {
    if (reset) {
      setSelectedOptions([])
      setFilteredQuantitative(0)
    }
  }, [reset])

  useEffect(() => {
    if (selectedOptionsDefaultValues) {
      const selectedOptionsDefault: any = []
      options.forEach((element) => {
        if (selectedOptionsDefaultValues.includes(element.value)) {
          selectedOptionsDefault.push({
            label: element.label,
            value: element.value,
          })
        }
      })
      setSelectedOptions(selectedOptionsDefault)
    }
  }, [options])

  return (
    <>
      <Button onClick={onOpen} variant="outline" w={fullWidth ? '100%' : 'auto'} isLoading={isLoading}>
        <Center gap={2}>
          <Center bg="primary" p={1} rounded="full" color="white" w="24px" h="24px">
            <Text>{filteredQuantitative}</Text>
          </Center>
          <Text>{label}</Text>
        </Center>
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size={['full', 'lg', 'xl', '2xl', '3xl']}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{label}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex flexDir="column" gap={4}>
              {selectedOptions.length > 0 && (
                <>
                  <Flex flexDir="column" gap={2}>
                    <Text fontSize="md" fontWeight="bold">
                      {label} selecionadas
                    </Text>
                    {selectedOptions.map((item, index) => (
                      <Flex
                        key={index}
                        bg="light"
                        p={1}
                        rounded="md"
                        cursor="pointer"
                        gap={2}
                        onClick={() => onDeselect(item)}
                        _hover={{ bg: 'red.50' }}
                      >
                        <Checkbox isChecked={true} />
                        <Text>{item.label}</Text>
                      </Flex>
                    ))}
                  </Flex>
                  <Divider />
                </>
              )}

              <Flex flexDir="column" gap={2}>
                <Text fontSize="md" fontWeight="bold">
                  {label} disponíveis
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Search color="gray.300" />
                  </InputLeftElement>
                  <Input type="text" value={filterInput} onChange={(event) => setFilterInput(event.target.value)} />
                </InputGroup>
                <Flex flexDir="column" maxH="300px" overflow="auto" bg="bgContainer" rounded="md" p={1} gap={2}>
                  {options.map((item, index) => {
                    if (!selectedOptions.some((e) => e.label === item.label)) {
                      if (filterInput != '') {
                        if (!item.label.toUpperCase().includes(filterInput.toUpperCase())) {
                          return <></>
                        }
                      }

                      return (
                        <Flex
                          key={index}
                          bg="light"
                          p={2}
                          rounded="md"
                          cursor="pointer"
                          gap={2}
                          onClick={() => onSelect(item)}
                          _hover={{ bg: 'lightPrimary' }}
                        >
                          <Checkbox />
                          {item?.label?.includes('null') || item?.label?.includes('NULL') ? (
                            <Text fontStyle="italic" variant="secondary">
                              {item?.label?.replace('null', 'Sem registro').replace('NULL', 'Sem registro')}
                            </Text>
                          ) : (
                            <Text>{item?.label}</Text>
                          )}
                        </Flex>
                      )
                    }
                  })}
                </Flex>
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme="red" onClick={cleanFilter} w="100%">
              Limpar filtros
            </Button>
            <Button w="100%" onClick={onSubmit}>
              Filtrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default memo(CustomMultipleItemSelection)
